import { useEffect, useRef, type ReactNode } from "react";

const FOCUSABLE_SELECTOR =
  'button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

// Codex findings review (panel-redesign-v3-04, #5): a caller may keep a
// step's fieldset MOUNTED but visually hidden via inline `display: none`
// (e.g. CampaignCreatePanel's Audiencia fieldset, kept mounted across steps
// so EmpresaSelector's Show-change validation effect survives step changes
// rather than resetting on unmount). `:not([disabled])` alone says nothing
// about visibility, so the focus trap below used to still offer a hidden
// fieldset's inputs as tab stops whenever one of them happened to be the
// FIRST or LAST element in DOM order (a hidden element in the MIDDLE of the
// list was already skipped in practice by real Tab traversal, which walks
// from the CURRENT element rather than recomputing first/last). Walking up
// from each candidate and checking inline `display: none` (no real layout
// engine is available in this project's jsdom test toolchain to ask
// `offsetParent`/`getClientRects` instead) keeps a hidden fieldset mounted
// -- preserving its effects -- while genuinely removing it from the trap.
function isVisible(element: HTMLElement): boolean {
  let node: HTMLElement | null = element;
  while (node) {
    if (node.style.display === "none") return false;
    node = node.parentElement;
  }
  return true;
}

function queryVisibleFocusable(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(isVisible);
}

/**
 * Generic popup dialog (user follow-up 2026-09-18: "Ver adjuntos"/"Reemplazar
 * contenido" should open as a popup styled like the rest of the app).
 * Generalizes `ConfirmDialog`'s already-established backdrop/focus-trap/
 * Escape/focus-restoration contract to arbitrary content instead of a fixed
 * message+Confirmar/Cancelar shape. `ConfirmDialog` itself is unchanged — a
 * distinct `alertdialog` semantic, not rebuilt on top of this.
 */
export function Modal({
  ariaLabel,
  onClose,
  children,
  restoreFocusTo,
  restoreFocusFallback,
  suspendFocusTrap = false,
}: {
  ariaLabel: string;
  onClose: () => void;
  children: ReactNode;
  /**
   * Remaining Codex PR #384 follow-up finding 6: a caller that opens this
   * dialog from a MENU ITEM inside its own dropdown (e.g.
   * `CampaignDetailScreen`'s "Más ▾" menu) typically closes that dropdown in
   * the SAME batched update that mounts this dialog -- the menu item is
   * already removed from the DOM by the time this effect runs, so
   * `document.activeElement` has already fallen back to `<body>` and the
   * generic "restore whatever had focus" behavior below restores nothing
   * useful. Pass the dropdown's own trigger element here to restore focus to
   * THAT instead when this dialog closes. Omitted (the default), this keeps
   * the generic `document.activeElement`-at-mount behavior every other
   * caller already relies on.
   */
  restoreFocusTo?: HTMLElement | null;
  /**
   * Codex finding (round 4, PR #381): the default restoration below always
   * refocuses whatever had focus when this dialog OPENED (or `restoreFocusTo`
   * above, when given) — but that target can become hidden/unusable by the
   * time the dialog CLOSES (e.g. a desktop-only button after the viewport
   * shrank below 768px), silently falling back to `<body>`. Called lazily in
   * the close/unmount cleanup (never at open time, so it always reflects the
   * LATEST state) — return a still-usable element to redirect focus there
   * instead, or `null`/`undefined` to keep the normal restoration.
   */
  restoreFocusFallback?: () => HTMLElement | null;
  /**
   * Codex round 6 (PR #383): while a nested dialog (e.g. a ConfirmDialog
   * rendered inside this modal's body) owns Tab handling, this modal's own
   * document-level Tab wrap must stand down — otherwise Shift+Tab from the
   * nested dialog's first control makes THIS handler move focus to the outer
   * footer before the nested handler runs. Escape handling is unaffected.
   */
  suspendFocusTrap?: boolean;
}) {
  const dialogRef = useRef<HTMLDivElement>(null);
  // Remaining Codex PR #384 follow-up finding 5: the keydown effect below
  // intentionally only runs ONCE on mount (`[]` deps) so the focus trap
  // doesn't re-bind (and steal focus back to the first element) on every
  // render. But some callers guard `onClose` with a mutable condition that
  // changes over the dialog's lifetime (e.g. `CampaignDetailScreen`'s action
  // modals: "don't close while the mutation is submitting") and re-pass a
  // NEW closure reflecting that latest condition on every render. Reading
  // `onClose` directly in the effect's closure would keep calling the FIRST
  // render's (stale) version forever. This ref is updated on every render
  // (not inside the effect) so the keydown handler always reads the latest.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const restoreFocusFallbackRef = useRef(restoreFocusFallback);
  restoreFocusFallbackRef.current = restoreFocusFallback;
  const suspendFocusTrapRef = useRef(suspendFocusTrap);
  suspendFocusTrapRef.current = suspendFocusTrap;

  useEffect(() => {
    const previouslyFocused = restoreFocusTo ?? (document.activeElement as HTMLElement | null);
    const focusable = dialogRef.current ? queryVisibleFocusable(dialogRef.current) : [];
    // The header "×" (ModalCloseButton) precedes the form in DOM order. In a
    // form popup, land on its first field instead of the close control; popups
    // with no field (buttons only) keep the first focusable, the close button.
    const isCloseButton = (element: HTMLElement) => element.classList.contains("v2-modal-v2__close");
    const firstField = focusable.find((element) => /^(INPUT|TEXTAREA|SELECT)$/.test(element.tagName));
    const initial = focusable[0] && isCloseButton(focusable[0]) ? (firstField ?? focusable[0]) : focusable[0];
    initial?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onCloseRef.current();
        return;
      }

      if (event.key === "Tab" && dialogRef.current && !suspendFocusTrapRef.current) {
        const focusableNow = queryVisibleFocusable(dialogRef.current);
        if (focusableNow.length === 0) return;
        const first = focusableNow[0]!;
        const last = focusableNow[focusableNow.length - 1]!;
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      const fallback = restoreFocusFallbackRef.current?.();
      (fallback ?? previouslyFocused)?.focus();
    };
    // Same rationale as ConfirmDialog: onClose is a fresh inline closure on
    // every render — only (re)bind when the dialog's own identity changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="v2-modal-backdrop">
      <div ref={dialogRef} className="v2-modal" role="dialog" aria-modal="true" aria-label={ariaLabel}>
        {children}
      </div>
    </div>
  );
}
